import { Dispatch, SetStateAction } from 'react'
import * as d3 from 'd3'
import theme from 'theme'
import { resolveThemeColor } from 'theme/config'
import { getActiveThemeMode as activeThemeMode } from 'theme/themeMode'

/* Utils */
import {
  ChartData,
  ChartDataExtended,
  CPIFrequency,
  CPIManifestUIArea,
  CPISeverity,
  CPITimePeriod,
  CustomLabelDate,
  D3Position,
  DataPoint,
  DataStatus,
  Label,
  LabelCount,
  SnowflakeChartData,
  VisibleLabel
} from 'models'
import {
  formatPerformanceUnitDisplay,
  formatTooltipDate,
  getUtcDateAndTime,
  instanceOfSnowflake
} from 'ui/pages/library/utils'
import { decorateMetricLabel, pointStateTitleHtml } from 'ui/pages/library/chartPointState'
import {
  ADD_EVENT_BUTTON_X_OFFSET,
  appendCartesianGridHorizontal,
  appendDefs,
  appendEventIndicators,
  appendSLAElements,
  appendXYAxis
} from './chart-components'
import { appendCartesianGridHorizontalOC, appendSlaElementsOC, appendXYAxisOC } from './chart-components-oc'
import {
  appendHealthLines,
  appendHoverPointCircles,
  appendHoverPointLine,
  appendIsolatedPointMarkers
} from './chart-components-shared'
import { checkIsGreen, getHealthDirection } from 'lib/utils'

export const MAX_PINNED_LABELS_COUNT = 3
export const redStroke = () => resolveThemeColor('status-value-bad', activeThemeMode())
export const greenStroke = () => resolveThemeColor('status-value-good', activeThemeMode())
export const chartHeightStart = 16
export const xAxisHeight = 80
export const CHART_HEIGHT = 350

export const multiplicativeValueCpis = [
  'cpi034',
  'cpi035',
  'cpi052',
  'cpi053',
  'cpi054',
  'cpi055',
  'cpi056',
  'cpi057',
  'cpi061',
  'cpi062'
]

export const getYLine = (chartData: ChartData | SnowflakeChartData | null | undefined, maxValueY: number) => {
  if (chartData && chartData.values && chartData.values.length > 0) {
    return chartData.sla
  }
  return maxValueY / 2
}

export const getLineColor = (
  chartData: ChartData | SnowflakeChartData | null | undefined,
  position: 'top' | 'bottom'
) => {
  if (!chartData) return ''

  let healthDirection: 'desc' | 'asc'
  if (instanceOfSnowflake(chartData)) {
    /* No recent point leaves nothing to place against the SLA, so no direction can be claimed. */
    if (!chartData.recentPoint) return ''
    healthDirection = getHealthDirection(
      chartData.zoneColor,
      Number(new Date(chartData.recentPoint.xValue).getTime()),
      chartData.sla
    )
  } else {
    healthDirection = getHealthDirection(
      chartData.zoneColor,
      Number(new Date(chartData.recentValue).getTime()),
      chartData.sla
    )
  }

  if (position === 'top') {
    if (healthDirection === 'desc') {
      return redStroke()
    } else {
      return greenStroke()
    }
  }
  if (position === 'bottom') {
    if (healthDirection === 'desc') {
      return greenStroke()
    } else {
      return redStroke()
    }
  }

  return redStroke()
}

export const getAreaFill = (chartData: ChartData | SnowflakeChartData | null | undefined) => {
  if (!chartData) return ''

  if (chartData.status === DataStatus.Initializing) {
    return '#yellowGradient'
  }
  if (chartData.status === DataStatus.Recalculating) {
    return '#blueGradient'
  }

  let recentValue: number
  if (instanceOfSnowflake(chartData)) {
    /* No recent point leaves nothing to place against the SLA, so neither zone can be claimed. */
    if (!chartData.recentPoint) return ''
    recentValue = Number(chartData.recentPoint.yValue)
  } else {
    recentValue = Number(chartData.recentValue)
  }
  if (typeof recentValue !== 'number') return ''
  if (typeof Number(chartData.sla) !== 'number') return ''

  const healthDirection = getHealthDirection(chartData.zoneColor, recentValue, chartData.sla)
  const isGreen = checkIsGreen(healthDirection, recentValue, chartData.sla)

  if (isGreen) {
    return '#greenGradient'
  } else {
    return '#redGradient'
  }
}

export const getAreaStroke = (chartData: ChartData | null | undefined, prediction = false) => {
  if (!chartData) return ''

  if (prediction) return '#BB95EA'
  if (chartData.status === DataStatus.Initializing) {
    return theme.colors.white
  }
  if (chartData.status === DataStatus.Recalculating) {
    return theme.baseColors.info[30]
  }

  if (typeof Number(chartData.recentValue) !== 'number') return ''
  if (typeof Number(chartData.sla) !== 'number') return ''

  const healthDirection = getHealthDirection(chartData.zoneColor, Number(chartData.recentValue), chartData.sla)
  const isGreen = checkIsGreen(healthDirection, Number(chartData.recentValue), chartData.sla)
  if (isGreen) {
    return greenStroke()
  } else {
    return redStroke()
  }
}

export const getDataStartsAt = (
  firstValue: string,
  weeklyGrouped: ChartDataExtended | null,
  selectedFrequency: CPIFrequency
) => {
  if (selectedFrequency === CPIFrequency.Weekly && weeklyGrouped) {
    return getUtcDateAndTime(weeklyGrouped.values[0].start).labelFormatDate
  }

  return getUtcDateAndTime(firstValue).labelFormatDate
}

interface ShowHoverElementsProps {
  svg:
    | d3.Selection<SVGSVGElement | null, unknown, null, undefined>
    | d3.Selection<SVGGElement, unknown, null, undefined>
  tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>
  dataPoint: DataPoint
  stripeWidth: number
  isHoveringXAxis: boolean
  /* Overrides the stripe the add-label button parks on; see positionAddEventButton. */
  addButtonIndex?: number
}

/*
 * Park the add-label button, and its hover tooltip, over one stripe. Separate from the hover
 * elements because a stripe the response never filled contributes no point to hover, and a label
 * can still be placed on a date the source has no reading for.
 * */
export const positionAddEventButton = ({
  svg,
  stripeWidth,
  index
}: {
  svg: ShowHoverElementsProps['svg']
  stripeWidth: number
  index: number
}) => {
  const stripeMiddle = stripeWidth * index + stripeWidth / 2
  const addButtonCx = stripeMiddle + ADD_EVENT_BUTTON_X_OFFSET

  svg.select('.add-button-circle').attr('display', 'block').attr('cx', addButtonCx)
  svg.select('.add-button-rect-hor').attr('x', addButtonCx - 5.5)
  svg.select('.add-button-rect-ver').attr('x', addButtonCx - 1.5)
  svg.select('.add-button-rect-tooltip-wrapper').attr('x', stripeMiddle - 45)
  svg.select('.add-button-rect-tooltip-text').attr('x', stripeMiddle - 39)
}

const showHoverElements = ({
  svg,
  tooltip,
  dataPoint,
  stripeWidth,
  isHoveringXAxis,
  addButtonIndex
}: ShowHoverElementsProps) => {
  /* Stripe index, not positions index: an empty stripe contributes no position, so the two diverge. */
  const index = dataPoint.monthIndex
  if (typeof index === 'undefined') return

  const circleInner = d3.select('.hover-circle-inner')
  const circleOuter = d3.select('.hover-circle-outer')

  positionAddEventButton({ svg, stripeWidth, index: addButtonIndex ?? index })

  /* Count Tooltip */
  const countRectTooltipWrapper = svg.select('.count-rect-tooltip-wrapper')
  countRectTooltipWrapper.attr('x', stripeWidth * index + stripeWidth / 2 - 65)
  const countRectTooltipText = svg.select('.count-rect-tooltip-text')
  countRectTooltipText.attr('x', stripeWidth * index + stripeWidth / 2 - 54 + 'px')

  if (!isHoveringXAxis) {
    tooltip.style('display', 'flex')
  }

  /* A zero is a real measurement (0 of 7 users inactive), so it gets a dot like any other point. */
  const hoverCircleDisplay = typeof dataPoint.value === 'number' ? 'block' : 'none'
  circleInner.attr('cx', dataPoint.x)
  circleInner.attr('cy', dataPoint.y)
  circleInner.attr('display', hoverCircleDisplay)
  circleOuter.attr('cx', dataPoint.x)
  circleOuter.attr('cy', dataPoint.y)
  circleOuter.attr('display', hoverCircleDisplay)

  const cursorLine = svg.select('.cursor-line')
  cursorLine.attr('x1', dataPoint.x).attr('x2', dataPoint.x).style('display', 'block')
}

interface OnMouseMoveProps extends ShowHoverElementsProps {
  event: any
  d: any
  selectedTimePeriod: CPITimePeriod
  healthUnit: string
  tooltipUnit: string
  selectedFrequency: CPIFrequency
  selectedSeverity: CPISeverity
  isNoSeveritiesCpi: boolean
  dataStartsAt: string
  ocTooltip?: boolean
  cpi?: string
  mouseX: number
  mouseY: number
  predictionTooltip?: boolean
}

/*
 * Hover Tooltip
 * */
export const onMouseMove = ({
  cpi,
  svg,
  tooltip,
  selectedTimePeriod,
  dataPoint,
  stripeWidth,
  isHoveringXAxis,
  event,
  d,
  healthUnit,
  tooltipUnit,
  selectedFrequency,
  selectedSeverity,
  isNoSeveritiesCpi,
  dataStartsAt,
  ocTooltip = false,
  mouseX,
  mouseY,
  predictionTooltip,
  addButtonIndex
}: OnMouseMoveProps) => {
  if (!d) return

  showHoverElements({ svg, tooltip, dataPoint, stripeWidth, isHoveringXAxis, addButtonIndex })

  let unitInfoText =
    healthUnit === 'percent'
      ? `% of ${tooltipUnit}: <span class='tooltip-value'>${d?.numerator ?? 0} / ${d?.denominator ?? 0}</span>`
      : `Number of ${tooltipUnit}: <span class='tooltip-value'>${d?.denominator ?? 0}</span>`

  if (['cpi024', 'cpi023', 'cpi022'].includes(<string>cpi)) {
    unitInfoText =
      healthUnit === 'percent'
        ? `Number of Emails: <span class='tooltip-value'>${d?.numerator ?? 0} / ${d?.denominator ?? 0}</span>`
        : `Number of ${tooltipUnit}: <span class='tooltip-value'>${d?.denominator ?? 0}</span>`
  } else if (cpi === 'cpi021') {
    unitInfoText =
      healthUnit === 'percent'
        ? `Number of Training Assignments: <span class='tooltip-value'>${d?.numerator ?? 0} / ${d?.denominator ?? 0}</span>`
        : `Number of ${tooltipUnit}: <span class='tooltip-value'>${d?.denominator ?? 0}</span>`
  }

  if (ocTooltip) {
    unitInfoText =
      healthUnit === 'percent'
        ? `% of ${tooltipUnit}: <span class='tooltip-value'>${d?.numerator ?? 0} / ${d?.denominator ?? 0}</span>`
        : `Number of open/closed ${tooltipUnit}: <span class='tooltip-value'>${d?.numerator ?? 0} / ${d?.denominator ?? 0}</span>`
  }

  unitInfoText = decorateMetricLabel(unitInfoText, d.pointState)
  const stateTitleHtml = pointStateTitleHtml(d.pointState)

  let formattedDate = formatTooltipDate(
    selectedFrequency === CPIFrequency.Weekly ? d.start : d.xValue,
    d.end,
    selectedFrequency,
    selectedTimePeriod,
    dataStartsAt
  )
  const splitDates = formattedDate.split('-')
  if (splitDates.length === 2 && splitDates[0] === splitDates[1]) {
    formattedDate = splitDates[0]
  }

  const pageX = mouseX
  const innerHeight = window?.innerHeight || 0
  const innerPageY = event.pageY
  const padding = 180
  const isBelowVisibleScreen = innerPageY + padding > innerHeight
  const pageY = isBelowVisibleScreen ? mouseY - 145 : mouseY

  if (predictionTooltip) {
    /* Both branches return before the shared classed() call below; clear a title left by an estimated/partial hover. */
    tooltip.classed('has-state-title', false)
    let left: string
    const top = pageY - 128 + 'px'

    if (d.prediction) {
      left = pageX < 375 ? pageX - 50 + 'px' : pageX - 310 + 'px'

      tooltip
        .html(
          `
            <div class="prediction-text-wrapper">
              <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_dd_1_3292)">
                <circle cx="18.5" cy="18.5" r="11.5" fill="url(#paint0_linear_1_3292)"/>
                <circle cx="18.5" cy="18.5" r="11.2946" stroke="#BB95EA" stroke-width="0.410714"/>
                </g>
                <g clip-path="url(#clip0_1_3292)">
                <path d="M21.9503 16.7749L22.6691 15.1937L24.2503 14.4749L22.6691 13.7562L21.9503 12.1749L21.2316 13.7562L19.6503 14.4749L21.2316 15.1937L21.9503 16.7749Z" fill="white"/>
                <path d="M21.9503 20.2249L21.2316 21.8062L19.6503 22.5249L21.2316 23.2437L21.9503 24.8249L22.6691 23.2437L24.2503 22.5249L22.6691 21.8062L21.9503 20.2249Z" fill="white"/>
                <path d="M17.6378 17.0624L16.2003 13.8999L14.7628 17.0624L11.6003 18.4999L14.7628 19.9374L16.2003 23.0999L17.6378 19.9374L20.8003 18.4999L17.6378 17.0624ZM16.7696 19.0692L16.2003 20.3227L15.6311 19.0692L14.3776 18.4999L15.6311 17.9307L16.2003 16.6772L16.7696 17.9307L18.0231 18.4999L16.7696 19.0692Z" fill="white"/>
                </g>
                <defs>
                <filter id="filter0_dd_1_3292" x="0.839286" y="0.839286" width="35.3214" height="35.3214" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset/>
                <feGaussianBlur stdDeviation="3.08036"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0.556863 0 0 0 0 0.305882 0 0 0 0 0.85098 0 0 0 1 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_3292"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset/>
                <feGaussianBlur stdDeviation="1.02679"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0.556863 0 0 0 0 0.305882 0 0 0 0 0.85098 0 0 0 1 0"/>
                <feBlend mode="normal" in2="effect1_dropShadow_1_3292" result="effect2_dropShadow_1_3292"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_1_3292" result="shape"/>
                </filter>
                <linearGradient id="paint0_linear_1_3292" x1="18.5" y1="7" x2="18.5" y2="30" gradientUnits="userSpaceOnUse">
                <stop stop-color="#010002"/>
                <stop offset="0.275" stop-color="#56406D"/>
                <stop offset="0.53"/>
                </linearGradient>
                <clipPath id="clip0_1_3292">
                <rect width="13.8" height="13.8" fill="white" transform="translate(11.0254 11.6)"/>
                </clipPath>
                </defs>
              </svg>
              <p class='tooltip-performance'>${formattedDate}</p>
            </div>
            <div class="tooltip-units-wrapper">
              <p class='tooltip-units-prediction'>Predicted Performance:</p>
              <p class='tooltip-units-prediction'>${Math.round((d.yValue + Number.EPSILON) * 100) / 100}%</p>
            </div>
          `
        )
        .style('left', left)
        .style('top', top)

      return
    } else {
      left = pageX < 375 ? pageX - 15 + 'px' : pageX - 265 + 'px'

      tooltip
        .html(
          `
            <p class='tooltip-performance prediction-date'>${formattedDate}<AIPredictionIcon /></p>
            <div class="tooltip-units-wrapper">
              <p class='tooltip-units-prediction'>Performance:</p>
              <p class='tooltip-units-prediction'>${Math.round((d.yValue + Number.EPSILON) * 100) / 100}%</p>
            </div>
          `
        )
        .style('left', left)
        .style('top', top)

      return
    }
  }

  if (isNoSeveritiesCpi) {
    tooltip
      .html(
        `<p class="tooltip-date">${formattedDate}</p>${stateTitleHtml}
              <p class='tooltip-performance'>
                ${decorateMetricLabel('Performance', d.pointState)}: <span class='tooltip-value'>${d.yValue}${formatPerformanceUnitDisplay(
          healthUnit,
          tooltipUnit
        )}</span>
              </p>
          <p class='tooltip-units'>${unitInfoText}</p>`
      )
      .style('left', pageX < 375 ? pageX + 20 + 'px' : pageX - 200 + 'px')
      .style('top', pageY + 25 + 'px')
  } else {
    let left = pageX < 375 ? pageX + 20 + 'px' : pageX - 232 + 'px'
    let top = pageY + 25 + 'px'

    if (ocTooltip) {
      left = pageX < 375 ? pageX + 20 + 'px' : pageX - 325 + 'px'
      top = pageY + 25 + 'px'
    }

    if (cpi === 'cpi061') {
      top = pageY - 40 + 'px'
      left = pageX < 375 ? pageX + 20 + 'px' : pageX - 325 + 'px'

      tooltip
        .html(
          `<p class="tooltip-date">${formattedDate}</p>${stateTitleHtml}
              <p class='tooltip-performance'>
                ${decorateMetricLabel(
                  'Performance',
                  d.pointState
                )} (<span class='severity-text'>${selectedSeverity}</span>): <span class='tooltip-value'>${d.yValue} Events</span>
              </p>
              <p class='tooltip-units'>${unitInfoText}</p>
              <p class='tooltip-units'>${decorateMetricLabel('Number of Allowed Events', d.pointState)}: ${
            d.numberAllowed ?? 0
          }</p>
              <p class='tooltip-units'>${decorateMetricLabel('Number of Blocked Event', d.pointState)}: ${
            d.numberBlocked ?? 0
          }</p>
              <p class='tooltip-units'>${decorateMetricLabel('Number of Other Events', d.pointState)}: ${
            d.numberOthers ?? 0
          }</p>
`
        )
        .attr('class', 'data-point-tooltip cpi-061')
        .style('left', left)
        .style('top', top)
    } else {
      tooltip
        .html(
          `<p class="tooltip-date">${formattedDate}</p>${stateTitleHtml}
              <p class='tooltip-performance'>
                ${decorateMetricLabel(
                  'Performance',
                  d.pointState
                )} (<span class='severity-text'>${selectedSeverity} ${tooltipUnit}</span>): <span class='tooltip-value'>${
            d.yValue
          }${formatPerformanceUnitDisplay(healthUnit, tooltipUnit)}</span>
              </p>
              <p class='tooltip-units'>${unitInfoText}</p>`
        )
        .style('left', left)
        .style('top', top)
    }
  }

  tooltip.classed('has-state-title', Boolean(stateTitleHtml))
}

interface AppendTrendsChartElementsProps {
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>
  tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>
  xScaleGroup: d3.ScaleBand<string>
  yScale: d3.ScaleLinear<number, number, never>
  pathStr: string
  isolatedPositions: D3Position[]
  uniqueMonths: string[]
  filteredData: any[]
  data: ChartData
  groupedByWeeks: ChartDataExtended | null
  pinnedLabels?: Label[]
  dataDates: LabelCount[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  selectedSeverity: CPISeverity
  width: number
  height: number
  chartHeightStart: number
  stripeWidth: number
  healthBorder: number
  healthUnit: string
  dataStartsAt: string
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  setMode: Dispatch<SetStateAction<string>>
  setCustomDate: Dispatch<SetStateAction<CustomLabelDate>>
  setRedirectToMode: Dispatch<SetStateAction<string>>
  chartData: ChartData | SnowflakeChartData | null | undefined
  visibleLabels: VisibleLabel[]
  sectionsDisplayName: string
  sectionsDisplayNameSingular: string
  isNoSeveritiesCpi: boolean
  hideEventIndicators?: boolean
  areas: CPIManifestUIArea[]
}

export const appendTrendsChartElements = ({
  svg,
  tooltip,
  xScaleGroup,
  sectionsDisplayName,
  sectionsDisplayNameSingular,
  isNoSeveritiesCpi,
  yScale,
  pathStr,
  isolatedPositions,
  uniqueMonths,
  filteredData,
  data,
  groupedByWeeks,
  pinnedLabels,
  dataDates,
  selectedTimePeriod,
  selectedFrequency,
  selectedSeverity,
  width,
  height,
  chartHeightStart,
  stripeWidth,
  healthUnit,
  healthBorder,
  dataStartsAt,
  setDrawerOpen,
  setMode,
  setCustomDate,
  setRedirectToMode,
  chartData,
  visibleLabels,
  hideEventIndicators,
  areas
}: AppendTrendsChartElementsProps) => {
  const { sla } = data

  const lineColorTop = getLineColor(chartData, 'top')
  const lineColorBottom = getLineColor(chartData, 'bottom')
  const areaFill = getAreaFill(chartData)
  const bottomArea = areas.find((c) => c.range.includes('0,$'))
  const topArea = areas.find((c) => c.range.includes('$,'))

  appendDefs(svg)
  appendHoverPointCircles(svg)
  appendHoverPointLine({ svg, tooltip, height, chartHeightStart })
  appendCartesianGridHorizontal({ svg, width, height, chartHeightStart })

  let colorBottom = 'red'
  if (bottomArea && bottomArea.color) {
    colorBottom = bottomArea.color
  }
  const colorRed = resolveThemeColor('chart-area-bad', activeThemeMode())
  const colorGreen = resolveThemeColor('chart-area-good', activeThemeMode())

  /*
   *  Filled Area
   * */
  const barBgTop = svg.select('.bar-bg-red')
  if (!barBgTop.empty()) barBgTop.remove()
  svg
    .selectAll('.bar-bg-red')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorRed : colorGreen)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-red' : 'bar-bg-green')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', healthBorder)
  const barBgBottom = svg.select('.bar-bg-green')
  if (!barBgBottom.empty()) barBgBottom.remove()
  svg
    .selectAll('.bar-bg-green')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorGreen : colorRed)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-green' : 'bar-bg-red')
    .attr('x', 0)
    .attr('y', healthBorder)
    .attr('height', height - healthBorder)
    .attr('width', width)

  const existingFilledAreaStroke = svg.selectAll('.filled-area-stroke')
  if (!existingFilledAreaStroke.empty()) existingFilledAreaStroke.remove()
  svg
    .append('path')
    .attr('fill', 'none')
    .attr('stroke', resolveThemeColor('text-primary', activeThemeMode()))
    .attr('d', `${pathStr}`)
    .attr('class', 'filled-area-stroke')
    .attr('stroke-width', 2)
    .attr('stroke-linejoin', 'bevel')
  appendIsolatedPointMarkers({
    svg,
    positions: isolatedPositions,
    color: resolveThemeColor('text-primary', activeThemeMode())
  })

  const existingBars = svg.selectAll('.bar')
  if (!existingBars.empty()) existingBars.remove()
  svg
    .selectAll('.bar')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', 'transparent')
    .attr('fill-opacity', '1')
    .attr('class', 'bar')
    .attr('x', (d) => {
      return xScaleGroup(d) || 0
    })
    .attr('y', 0)
    .attr('width', xScaleGroup.bandwidth())
    .attr('height', height)
    .attr('stroke', theme.colors.surfaceContainerBackground)
    .attr('stroke-width', 4)
    .attr('clip-path', `inset(0px 0px 0px ${stripeWidth}px`)

  appendHealthLines({
    svg,
    height,
    chartHeightStart,
    healthBorder,
    lineColorTop: topArea ? (topArea.color === 'red' ? redStroke() : greenStroke()) : lineColorTop,
    lineColorBottom: bottomArea ? (bottomArea.color === 'green' ? greenStroke() : redStroke()) : lineColorBottom
  })
  appendSLAElements({
    svg,
    sectionsDisplayName,
    sectionsDisplayNameSingular,
    isNoSeveritiesCpi,
    selectedSeverity,
    width,
    height,
    healthBorder,
    sla,
    healthUnit,
    visibleLabels,
    areaFill: areaFill.includes('red') ? resolveThemeColor('status-value-good', activeThemeMode()) : resolveThemeColor('sla-line-bad', activeThemeMode())
  })
  appendXYAxis({
    svg,
    xScaleGroup,
    yScale,
    data,
    filteredData,
    selectedTimePeriod,
    selectedFrequency,
    height,
    healthUnit
  })
  if (!hideEventIndicators) {
    appendEventIndicators({
      svg,
      uniqueMonths,
      pinnedLabels,
      selectedTimePeriod,
      selectedFrequency,
      filteredData,
      groupedByWeeks,
      dataDates,
      height,
      stripeWidth,
      setDrawerOpen,
      setMode,
      setCustomDate,
      setRedirectToMode,
      dataStartsAt,
      status: chartData?.status
    })
  }
}

interface AppendPredictionTrendsChartElementsProps {
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>
  tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>
  xScaleGroup: d3.ScaleBand<string>
  yScale: d3.ScaleLinear<number, number, never>
  pathStr: string
  pathStrPrediction: string
  uniqueMonths: string[]
  filteredData: any[]
  data: ChartData
  pinnedLabels?: Label[]
  dataDates: LabelCount[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  selectedSeverity: CPISeverity
  width: number
  height: number
  chartHeightStart: number
  stripeWidth: number
  healthBorder: number
  healthUnit: string
  getAreaFill: (chartData: ChartData | null | undefined) => string
  getAreaStroke: (chartData: ChartData | null | undefined, prediction?: boolean) => string
  getLineColor: (chartData: ChartData | null | undefined, position: 'top' | 'bottom') => string
  chartData: ChartData | null | undefined
  visibleLabels: VisibleLabel[]
  sectionsDisplayName: string
  sectionsDisplayNameSingular: string
  isNoSeveritiesCpi: boolean
  hideEventIndicators?: boolean
}

export const appendPredictionTrendsChartElements = ({
  svg,
  tooltip,
  xScaleGroup,
  sectionsDisplayName,
  sectionsDisplayNameSingular,
  isNoSeveritiesCpi,
  yScale,
  pathStr,
  pathStrPrediction,
  uniqueMonths,
  filteredData,
  data,
  selectedTimePeriod,
  selectedFrequency,
  selectedSeverity,
  width,
  height,
  chartHeightStart,
  stripeWidth,
  healthUnit,
  healthBorder,
  getAreaStroke,
  getLineColor,
  chartData,
  visibleLabels
}: AppendPredictionTrendsChartElementsProps) => {
  const { sla } = data
  const lineColorTop = getLineColor(chartData, 'top')
  const lineColorBottom = getLineColor(chartData, 'bottom')
  const areaStroke = getAreaStroke(chartData)
  const predictionAreaStroke = getAreaStroke(chartData, true)
  const areaFill = getAreaFill(chartData)

  appendDefs(svg)
  appendHoverPointCircles(svg)
  appendHoverPointLine({ svg, tooltip, height, chartHeightStart })
  appendCartesianGridHorizontal({ svg, width, height, chartHeightStart })

  const existingFilledAreaStroke = svg.selectAll('.filled-area-stroke')
  if (!existingFilledAreaStroke.empty()) existingFilledAreaStroke.remove()

  const existingFilledAreaStroke2 = svg.selectAll('.filled-area-stroke-prediction')
  if (!existingFilledAreaStroke2.empty()) existingFilledAreaStroke2.remove()

  svg
    .append('path')
    .attr('fill', 'none')
    .attr('stroke', '#fff')
    .attr('d', pathStr)
    .attr('class', 'filled-area-stroke')
    .attr('stroke-width', 3)
    .attr('stroke-linejoin', 'bevel')
  svg
    .append('path')
    .attr('fill', 'none')
    .attr('stroke', `${predictionAreaStroke}`)
    .attr('d', `${pathStrPrediction.replace('L', 'M')}`)
    .attr('class', 'filled-area-stroke-prediction')
    .attr('stroke-width', 3)
    .attr('stroke-linejoin', 'bevel')

  let colorBottom = 'red'
  if (areaFill.includes('green')) {
    colorBottom = '#FF7F6B'
  }
  const colorGreen = 'rgba(102, 41, 45, 0.6)'
  const colorRed = 'rgba(50, 85, 36, 0.6)'

  /*
   *  Filled Area
   * */
  const barBgRed = svg.select('.bar-bg-red')
  if (!barBgRed.empty()) barBgRed.remove()
  svg
    .selectAll('.bar-bg-red')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorRed : colorGreen)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-red' : 'bar-bg-green')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', healthBorder)
  const barBgGreen = svg.select('.bar-bg-green')
  if (!barBgGreen.empty()) barBgGreen.remove()
  svg
    .selectAll('.bar-bg-green')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorGreen : colorRed)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-green' : 'bar-bg-red')
    .attr('x', 0)
    .attr('y', healthBorder)
    .attr('height', height - healthBorder)
    .attr('width', width)

  const existingBars = svg.selectAll('.bar')
  if (!existingBars.empty()) existingBars.remove()
  svg
    .selectAll('.bar')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', 'transparent')
    .attr('fill-opacity', '1')
    .attr('class', 'bar')
    .attr('x', (d) => {
      return xScaleGroup(d) || 0
    })
    .attr('y', 0)
    .attr('width', xScaleGroup.bandwidth())
    .attr('height', height)
    .attr('stroke', theme.colors.surfaceContainerBackground)
    .attr('stroke-width', 4)
    .attr('clip-path', `inset(0px 0px 0px ${stripeWidth}px`)

  appendHealthLines({ svg, height, chartHeightStart, healthBorder, lineColorTop, lineColorBottom })
  appendSLAElements({
    svg,
    sectionsDisplayName,
    sectionsDisplayNameSingular,
    isNoSeveritiesCpi,
    selectedSeverity,
    width,
    height,
    healthBorder,
    sla,
    healthUnit,
    visibleLabels,
    areaFill: areaStroke
  })
  appendXYAxis({
    svg,
    xScaleGroup,
    yScale,
    data,
    filteredData,
    selectedTimePeriod,
    selectedFrequency,
    height,
    healthUnit
  })
}

interface AppendTrendsChartOCElementsProps {
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>
  tooltip: d3.Selection<d3.BaseType, unknown, HTMLElement, any>
  xScaleGroup: d3.ScaleBand<string>
  yScale: d3.ScaleLinear<number, number, never>
  groupedByWeeks: ChartDataExtended | null
  uniqueMonths: string[]
  selectedTimePeriod: CPITimePeriod
  selectedFrequency: CPIFrequency
  selectedSeverity: CPISeverity
  healthBorder: number
  filteredData: any[]
  dataStartsAt: string
  width: number
  height: number
  chartHeightStart: number
  healthUnit: string
  noData: boolean
  stripeWidth: number
  setCustomDate: ({ date, start, end }: CustomLabelDate) => void
  setRedirectToMode: Dispatch<SetStateAction<string>>
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  setMode: Dispatch<SetStateAction<string>>
  getLineColor: (chartData: ChartData | null | undefined, position: 'top' | 'bottom') => string
  getAreaStroke: (chartData: ChartData | null | undefined) => string
  pathStr: string
  isolatedPositions: D3Position[]
  pinnedLabels?: Label[]
  dataDates: LabelCount[]
  data: ChartData
  chartData: ChartData | null | undefined
  visibleLabels: VisibleLabel[]
  sectionsDisplayName: string
  sectionsDisplayNameSingular: string
}

export const appendTrendsChartOCElements = ({
  svg,
  tooltip,
  xScaleGroup,
  yScale,
  uniqueMonths,
  selectedTimePeriod,
  sectionsDisplayName,
  sectionsDisplayNameSingular,
  selectedFrequency,
  selectedSeverity,
  groupedByWeeks,
  filteredData,
  dataStartsAt,
  width,
  height,
  chartHeightStart,
  healthUnit,
  noData,
  stripeWidth,
  setCustomDate,
  healthBorder,
  setRedirectToMode,
  setDrawerOpen,
  setMode,
  getLineColor,
  getAreaStroke,
  pathStr,
  isolatedPositions,
  pinnedLabels,
  dataDates,
  data,
  chartData,
  visibleLabels
}: AppendTrendsChartOCElementsProps) => {
  const { sla } = data
  const areaStroke = getAreaStroke(chartData)
  const lineColorTop = getLineColor(chartData, 'top')
  const lineColorBottom = getLineColor(chartData, 'bottom')
  const areaFill = getAreaFill(chartData)

  appendHoverPointCircles(svg)
  appendHoverPointLine({ svg, tooltip, height, chartHeightStart })

  let colorBottom = 'red'
  if (areaFill.includes('green')) {
    colorBottom = '#FF7F6B'
  }
  const colorGreen = resolveThemeColor('chart-area-bad', activeThemeMode())
  const colorRed = resolveThemeColor('chart-area-good', activeThemeMode())
  /*
   *  Filled Area
   * */
  const barBgRed = svg.select('.bar-bg-red')
  if (!barBgRed.empty()) barBgRed.remove()
  svg
    .selectAll('.bar-bg-red')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorRed : colorGreen)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-red' : 'bar-bg-green')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', healthBorder)
  const barBgGreen = svg.select('.bar-bg-green')
  if (!barBgGreen.empty()) barBgGreen.remove()
  svg
    .selectAll('.bar-bg-green')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', colorBottom === 'green' ? colorGreen : colorRed)
    .attr('fill-opacity', '1')
    .attr('class', colorBottom === 'green' ? 'bar-bg-green' : 'bar-bg-red')
    .attr('x', 0)
    .attr('y', healthBorder)
    .attr('height', height - healthBorder)
    .attr('width', width)

  const existingFilledAreaStroke = svg.selectAll('.filled-area-stroke')
  if (!existingFilledAreaStroke.empty()) existingFilledAreaStroke.remove()
  svg
    .append('path')
    .attr('fill', 'none')
    .attr('stroke', resolveThemeColor('text-primary', activeThemeMode()))
    .attr('d', `${pathStr}`)
    .attr('class', 'filled-area-stroke')
    .attr('stroke-width', 2)
    .attr('stroke-linejoin', 'bevel')
  appendIsolatedPointMarkers({
    svg,
    positions: isolatedPositions,
    color: resolveThemeColor('text-primary', activeThemeMode())
  })

  const existingBars = svg.selectAll('.bar')
  if (!existingBars.empty()) existingBars.remove()
  svg
    .selectAll('.bar')
    .data(uniqueMonths)
    .enter()
    .append('rect')
    .attr('fill', 'transparent')
    .attr('fill-opacity', '1')
    .attr('class', 'bar')
    .attr('x', (d) => {
      return xScaleGroup(d) || 0
    })
    .attr('y', 0)
    .attr('width', xScaleGroup.bandwidth())
    .attr('height', height)
    .attr('stroke', theme.colors.surfaceContainerBackground)
    .attr('stroke-width', 4)
    .attr('clip-path', `inset(0px 0px 0px ${stripeWidth}px`)

  if (!noData) {
    appendHealthLines({ svg, height, chartHeightStart, healthBorder, lineColorTop, lineColorBottom })
  }
  appendSlaElementsOC({
    svg,
    sectionsDisplayName,
    sectionsDisplayNameSingular,
    selectedSeverity,
    width,
    height,
    healthBorder,
    sla,
    healthUnit,
    visibleLabels,
    areaFill: areaStroke
  })
  appendCartesianGridHorizontalOC({ svg, width, height, chartHeightStart })
  appendXYAxisOC({
    svg,
    data,
    xScaleGroup,
    yScale,
    selectedTimePeriod,
    selectedFrequency,
    filteredData,
    height,
    healthUnit,
    noData
  })
  appendEventIndicators({
    svg,
    uniqueMonths,
    pinnedLabels,
    selectedTimePeriod,
    selectedFrequency,
    filteredData,
    groupedByWeeks,
    dataDates,
    height,
    stripeWidth,
    setDrawerOpen,
    setMode,
    setCustomDate,
    setRedirectToMode,
    dataStartsAt,
    status: chartData?.status
  })
}
